"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { toast } from "react-hot-toast";
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  StarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  VideoCameraIcon,
  ChevronDownIcon,
  SparklesIcon,
  QuestionMarkCircleIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { Footer } from "@/components/Footer";
import { INDIAN_STATES, getCitiesByStateName } from "@/lib/indian-locations";
import LegalDocumentModal from "@/components/legal/LegalDocumentModal";

// Types for form
interface MvpRegistrationForm {
  fullName: string;
  email: string;
  password: string;
  phoneNumber: string;
  city: string;
  state: string;
  expertise: string[]; // now an array
  yearsOfExperience: string;
  termsAccepted: boolean;
}

const EXPERTISE_OPTIONS = [
  { value: "yogaInstructor", label: "Yoga Instructor" },
  { value: "meditationCoach", label: "Meditation Coach" },
  { value: "pranayamaCoach", label: "Pranayama Coach" },
  { value: "zumbaInstructor", label: "Zumba Instructor" },
  { value: "nutritionist", label: "Nutritionist" },
  { value: "ayurvedaPractitioner", label: "Ayurveda Practitioner" },
  { value: "womenWellnessCoach", label: "Women's Wellness Coach" },
  { value: "selfDefenseInstructor", label: "Self Defense Instructor" },
];

const InstructorLandingPage = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<MvpRegistrationForm>({
    fullName: "",
    email: "",
    password: "",
    phoneNumber: "",
    city: "",
    state: "",
    expertise: [],
    yearsOfExperience: "",
    termsAccepted: false,
  });

  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalDocType, setLegalDocType] = useState<"terms" | "privacy">("terms");

  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [isStateOpen, setIsStateOpen] = useState(false);
  const [isCityOpen, setIsCityOpen] = useState(false);
  const [stateSearch, setStateSearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [isExpertiseOpen, setIsExpertiseOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        // Show bar when hero is NOT intersecting (scrolled out of view)
        setShowStickyBar(!entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0, // Trigger as soon as even 1 pixel is out, or wait? 
        // User said "section will go out of ui view". 
        // Usually means mostly out. Let's stick with !isIntersecting which triggers when it LEAVES the viewport completely.
        // Or if we want it to trigger when the bottom passes the top?
        // default threshold 0 triggers when the first pixel enters or the last pixel leaves.
        // So !isIntersecting means the whole target is outside root.
        // But it could be above or below. 
        // Since we start inside, !isIntersecting usually means we scrolled past it.
      }
    );

    if (heroRef.current) {
      observer.observe(heroRef.current);
    }

    return () => {
      if (heroRef.current) {
        observer.unobserve(heroRef.current);
      }
    };
  }, []);

  const filteredStates = INDIAN_STATES.filter((state) =>
    state.toLowerCase().includes(stateSearch.toLowerCase())
  );

  const filteredCities = availableCities.filter((city) =>
    city.toLowerCase().includes(citySearch.toLowerCase())
  );

  const handleInputChange = (field: keyof MvpRegistrationForm, value: string | boolean | string[]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleExpertiseToggle = (value: string) => {
    setFormData((prev) => {
      const current = prev.expertise;
      const isSelected = current.includes(value);

      if (isSelected) {
        return { ...prev, expertise: current.filter(item => item !== value) };
      } else {
        return { ...prev, expertise: [...current, value] };
      }
    });
  };

  const selectState = (state: string) => {
    setFormData((prev) => ({ ...prev, state: state, city: "" }));
    const cities = getCitiesByStateName(state);
    setAvailableCities(cities);
    setIsStateOpen(false);
  };

  const selectCity = (city: string) => {
    setFormData((prev) => ({ ...prev, city }));
    setIsCityOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Basic Validation
    if (
      !formData.fullName ||
      !formData.email ||
      !formData.password ||
      !formData.phoneNumber ||
      !formData.state ||
      !formData.city ||
      formData.expertise.length === 0 ||
      !formData.termsAccepted
    ) {
      toast.error("Please fill in all required fields and accept the terms.");
      setLoading(false);
      return;
    }

    try {
      // Use existing registration API (modified to accept MVP fields)
      const response = await fetch("/api/instructors/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          password: formData.password,
          phoneNumber: formData.phoneNumber,
          city: formData.city,
          state: formData.state,
          instructorType: formData.expertise, // Already an array
          yearsOfExperience: parseInt(formData.yearsOfExperience) || 0,
          classFormat: "Online", // Defaulting as removed from UI
          bio: "", // Defaulting as removed from UI
          termsAccepted: formData.termsAccepted,
          gender: "", // Optional in backend now
          dateOfBirth: "", // Optional in backend now
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Registration successful! Welcome to Relaxing Routine.");
        // Redirect to profile or dashboard
        if (data.data?.token) {
          localStorage.setItem("auth_token", data.data.token);
          localStorage.setItem("user", JSON.stringify(data.data.user));
          router.push("/instructors/profile");
        } else {
          router.push("/login?role=instructor");
        }
      } else {
        toast.error(data.error || "Registration failed. Please try again.");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-white mt-[60px] sm:mt-[80px]">
        {/* Mobile Sticky CTA Bar */}
        <div
          className={`md:hidden fixed top-[60px] left-0 w-full z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 px-4 py-3 shadow-sm flex items-center justify-between gap-4 transition-all duration-300 transform ${showStickyBar ? "translate-y-0 opacity-100" : "-translate-y-10 opacity-0 pointer-events-none"
            }`}
        >
          <span className="text-sm font-semibold text-[#463a33]">Become an Instructor</span>
          <a
            href="#register-form"
            className="bg-[#ff7043] text-white text-sm font-bold px-4 py-2 rounded-lg hover:bg-[#ff5722] transition-colors"
          >
            Apply Now
          </a>
        </div>

        {/* HERO SECTION */}
        <section ref={heroRef} className="relative w-full py-12 sm:py-16 md:py-20 bg-white overflow-hidden zoom-container">
          {/* Subtle Background Elements from AboutUs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
            <div className="absolute top-[-20%] left-[-10%] w-[300px] sm:w-[400px] h-[300px] sm:h-[400px] bg-gradient-to-r from-[#ff7043]/10 to-purple-500/10 rounded-full mix-blend-multiply filter blur-3xl" />
            <div className="absolute bottom-[-20%] right-[-10%] w-[250px] sm:w-[350px] h-[250px] sm:h-[350px] bg-gradient-to-r from-blue-500/10 to-[#463a33]/10 rounded-full mix-blend-multiply filter blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 sm:px-5 py-2 sm:py-3 mb-6 sm:mb-8 border border-gray-200 shadow-lg">
                <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
                <span className="text-gray-700 text-xs sm:text-sm font-medium">
                  For Wellness Professionals
                </span>
              </div>

              <h1 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-2 sm:mb-3 font-sans tracking-tight leading-tight">
                Grow Your Practice with
                <span className="text-[#ff7043] font-medium ml-2">
                  Relaxing Routine
                </span>
              </h1>

              <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-6 sm:mb-8 opacity-80"></div>

              <div className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-4xl mx-auto space-y-8 text-center">
                <p>
                  At RelaxingRoutine, we empower passionate instructors to{" "}
                  <strong className="text-gray-800">teach what they love</strong>
                  , reach students beyond your local area and build a thriving digital
                  wellness business—without the technical headaches.
                </p>

                <ul className="grid sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
                  {[
                    "Wider student reach through our platform",
                    "Flexible Schedule",
                    "No upfront setup fees",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-center gap-2 font-medium text-gray-700 bg-gray-50 px-4 py-3 rounded-xl border border-gray-100"
                    >
                      <span className="text-orange-500 font-bold text-lg">
                        ✔
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
                  <a
                    href="#register-form"
                    className="group relative px-6 py-3 bg-gradient-to-r from-[#ff7043] to-[#ff5722] text-white font-bold tracking-wide rounded-xl shadow-[0_4px_10px_-4px_rgba(255,112,67,0.5)] hover:shadow-[0_8px_16px_-4px_rgba(255,112,67,0.6)] hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto text-center flex items-center justify-center gap-2 overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 rounded-xl"></div>
                    <CheckCircleIcon className="w-5 h-5 stroke-[2.5] relative z-10 group-hover:scale-110 transition-transform" />
                    <span className="relative z-10 text-sm sm:text-base">Apply as Instructor</span>
                  </a>
                  <a
                    href="#how-it-works"
                    className="group relative px-6 py-3 bg-white text-[#ff7043] font-bold tracking-wide border-2 border-[#ff7043]/20 hover:border-[#ff7043] rounded-xl shadow-[0_4px_10px_-4px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_16px_-4px_rgba(255,112,67,0.15)] hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto text-center flex items-center justify-center gap-2"
                  >
                    <StarIcon className="w-5 h-5 stroke-[2.5] group-hover:rotate-12 transition-transform text-[#ff7043]" />
                    <span className="text-sm sm:text-base">How it Works</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY INSTRUCTORS SHOULD JOIN */}
        <section className="py-10 sm:py-20 lg:py-28 bg-white relative overflow-hidden zoom-container">
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center mb-16">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-3 font-sans tracking-tight">
                Why Top Instructors Choose
                <span className="text-[#ff7043] font-medium ml-2">
                  Relaxing Routine
                </span>
              </h2>
              <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-6 opacity-80"></div>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-2xl mx-auto">
                Join a community that empowers you to teach what you love, on your
                terms. We handle the tech so you can focus on the teaching.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
              {[
                {
                  name: "Nationwide Reach, Local Impact",
                  description:
                    "Expand your student base beyond your city. Connect with wellness seekers from all over the country looking for your expertise.",
                  icon: VideoCameraIcon,
                  color: "blue",
                },
                {
                  name: "Freedom & Flexibility",
                  description:
                    "Set your own schedule, prices, and class formats. Run your live sessions exactly how you want.",
                  icon: ClockIcon,
                  color: "purple",
                },
                {
                  name: "Transparent Earnings (80 / 20 Split)",
                  description: (
                    <span>
                      Instructors earn 80% of class fees, while Relaxing Routine retains a 20% platform fee for payments, infrastructure, and student support.
                    </span>
                  ),
                  icon: CurrencyDollarIcon,
                  color: "green",
                },
                {
                  name: "Career Growth Tools",
                  description:
                    "Access pro-level analytics to refine your teaching, boost profile visibility, and effortless tools to manage your classes, schedule, and earnings.",
                  icon: StarIcon,
                  color: "orange",
                },
              ].map((feature, index) => (
                <div
                  key={feature.name}
                  className="group bg-white p-8 rounded-2xl border border-gray-100 shadow-[0_2px_8px_-2px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_24px_-6px_rgba(0,0,0,0.1)] hover:border-orange-100 transition-all duration-300"
                >
                  <div
                    className={`h-14 w-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 ${feature.color === "blue"
                      ? "bg-blue-50 text-blue-600"
                      : feature.color === "purple"
                        ? "bg-purple-50 text-purple-600"
                        : feature.color === "green"
                          ? "bg-green-50 text-green-600"
                          : "bg-orange-50 text-orange-600"
                      }`}
                  >
                    <feature.icon className="h-7 w-7 stroke-2" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#463a33] mb-3 group-hover:text-[#ff7043] transition-colors">
                    {feature.name}
                  </h3>
                  <div className="text-gray-600 leading-relaxed text-sm sm:text-base">
                    {feature.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO CAN APPLY */}
        <section className="bg-white py-10 sm:py-20 lg:py-28 text-center zoom-container">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto mb-12">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-3 font-sans tracking-tight">
                Who Can
                <span className="text-[#ff7043] font-medium ml-2">
                  Apply?
                </span>
              </h2>
              <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-6 opacity-80"></div>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-2xl mx-auto">
                We welcome passionate experts from various wellness fields.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
              {[
                "Yoga Instructors",
                "Meditation Coaches",
                "Pranayama Coaches",
                "Zumba / Fitness Trainers",
                "Nutritionists",
                "Ayurveda Practitioners",
                "Women's Wellness Coaches",
                "Self Defense Instructors"
              ].map((category, index) => (
                <div
                  key={index}
                  className="group relative bg-white rounded-xl p-4 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-3 overflow-hidden"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                    <CheckCircleIcon className="h-5 w-5 text-orange-500" />
                  </div>
                  <span className="font-medium text-gray-700 text-sm sm:text-base text-left">{category}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-gray-500 text-xs sm:text-sm italic">
              Acceptance is subject to profile review and platform guidelines.
            </p>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section
          id="how-it-works"
          className="w-full py-10 sm:py-16 md:py-20 bg-gradient-to-b from-gray-50 to-white"
        >
          <div className="container mx-auto px-4 zoom-container">
            <div className="text-center mb-12 sm:mb-16">
              <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-2 sm:mb-3 font-sans tracking-tight leading-tight">
                Get Started in
                <span className="text-[#ff7043] font-medium ml-2">3 Steps</span>
              </h2>
              <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-4 sm:mb-6 opacity-80"></div>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-2xl mx-auto">
                Simple process. No complicated paperwork. Start your journey in minutes.
              </p>
            </div>

            <div className="max-w-5xl mx-auto">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Step 1 */}
                <div className="relative">
                  <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#ff7043] transition-colors h-full shadow-sm hover:shadow-md">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 shadow-orange-200 shadow-lg">
                      1
                    </div>
                    <h3 className="text-base font-semibold text-[#463a33] mb-2">
                      Register & Profile
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      Fill out the simple form below. Tell us about your expertise, experience, and what you love to teach.
                    </p>
                  </div>

                  <div className="hidden md:block absolute top-6 -right-3 w-6 h-0.5 bg-gradient-to-r from-orange-300 to-transparent z-10"></div>
                </div>

                {/* Step 2 */}
                <div className="relative">
                  <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#ff7043] transition-colors h-full shadow-sm hover:shadow-md">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 shadow-blue-200 shadow-lg">
                      2
                    </div>
                    <h3 className="text-base font-semibold text-[#463a33] mb-2">
                      Profile Review
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      We review submitted details to maintain platform quality. Once verified, you earn the 'Verified' badge to boost student trust.
                    </p>
                  </div>
                  <div className="hidden md:block absolute top-6 -right-3 w-6 h-0.5 bg-gradient-to-r from-blue-300 to-transparent z-10"></div>
                </div>

                {/* Step 3 */}
                <div className="relative">
                  <div className="bg-white p-6 rounded-xl border border-gray-200 hover:border-[#ff7043] transition-colors h-full shadow-sm hover:shadow-md">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-xl mb-4 shadow-green-200 shadow-lg">
                      3
                    </div>
                    <h3 className="text-base font-semibold text-[#463a33] mb-2">
                      Start Teaching
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      List your classes, set your own prices, and start welcoming your first students from our platform.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>



        {/* REGISTRATION FORM */}
        <div id="register-form" className="py-10 sm:py-20 lg:py-28 bg-gradient-to-t from-orange-50 to-white relative isolate overflow-hidden">
          {/* Decorational pattern */}
          <div className="zoom-container">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none"
              style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                backgroundSize: '32px 32px'
              }}
            ></div>

            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center mb-12">
                <h2 className="text-lg sm:text-xl md:text-2xl font-semibold text-[#463a33] mb-3 font-sans tracking-tight">
                  Ready to
                  <span className="text-[#ff7043] font-medium ml-2">
                    Start?
                  </span>
                </h2>
                <div className="w-12 sm:w-16 h-0.5 bg-[#ff7043] mx-auto mb-6 opacity-80"></div>
                <p className="text-gray-600 text-sm sm:text-base md:text-lg leading-relaxed font-normal max-w-2xl mx-auto">
                  Join our community of expert instructors today.
                </p>
              </div>

              <div className="mx-auto max-w-3xl">

                <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                      <div className="sm:col-span-2">
                        <label
                          htmlFor="fullName"
                          className="block text-sm font-semibold leading-6 text-gray-900"
                        >
                          Full Name *
                        </label>
                        <div className="mt-2.5 relative">
                          <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            name="fullName"
                            id="fullName"
                            value={formData.fullName}
                            onChange={(e) =>
                              handleInputChange("fullName", e.target.value)
                            }
                            className="block w-full rounded-xl border-0 py-3 pl-11 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 bg-gray-50 focus:bg-white transition-all sm:text-sm sm:leading-6"
                            placeholder="John Doe"
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-semibold leading-6 text-gray-900"
                        >
                          Email *
                        </label>
                        <div className="mt-2.5 relative">
                          <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={(e) =>
                              handleInputChange("email", e.target.value)
                            }
                            className="block w-full rounded-xl border-0 py-3 pl-11 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 bg-gray-50 focus:bg-white transition-all sm:text-sm sm:leading-6"
                            placeholder="john@example.com"
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="phoneNumber"
                          className="block text-sm font-semibold leading-6 text-gray-900"
                        >
                          Phone Number *
                        </label>
                        <div className="mt-2.5 relative">
                          <PhoneIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="tel"
                            name="phoneNumber"
                            id="phoneNumber"
                            value={formData.phoneNumber}
                            onChange={(e) =>
                              handleInputChange("phoneNumber", e.target.value)
                            }
                            className="block w-full rounded-xl border-0 py-3 pl-11 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 bg-gray-50 focus:bg-white transition-all sm:text-sm sm:leading-6"
                            placeholder="9876543210"
                          />
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-semibold leading-6 text-gray-900"
                        >
                          Password *
                        </label>
                        <div className="mt-2.5">
                          <input
                            type="password"
                            name="password"
                            id="password"
                            value={formData.password}
                            onChange={(e) =>
                              handleInputChange("password", e.target.value)
                            }
                            className="block w-full rounded-xl border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 bg-gray-50 focus:bg-white transition-all sm:text-sm sm:leading-6"
                            placeholder="Min 8 characters"
                          />
                        </div>
                      </div>

                      {/* State Dropdown - Custom */}
                      <div className="relative">
                        <label className="block text-sm font-semibold leading-6 text-gray-900">
                          State *
                        </label>
                        <div className="mt-2.5 relative">
                          <button
                            type="button"
                            onClick={() => {
                              setIsStateOpen(!isStateOpen);
                              setStateSearch("");
                            }}
                            className="relative w-full cursor-default rounded-xl border-0 py-3 pl-11 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm sm:leading-6 bg-gray-50 focus:bg-white transition-all"
                          >
                            <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <span className={`block truncate ${!formData.state && "text-gray-500"}`}>
                              {formData.state || "Select State"}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            </span>
                          </button>

                          {isStateOpen && (
                            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                              <div className="sticky top-0 z-10 bg-white px-2 py-2 border-b border-gray-100">
                                <input
                                  type="text"
                                  autoFocus
                                  className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                                  placeholder="Search state..."
                                  value={stateSearch}
                                  onChange={(e) => setStateSearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              {filteredStates.map((state) => (
                                <div
                                  key={state}
                                  className="relative cursor-default select-none py-2 pl-3 pr-9 hover:bg-orange-50 hover:text-orange-900 text-gray-900 cursor-pointer"
                                  onClick={() => {
                                    selectState(state);
                                    setStateSearch("");
                                  }}
                                >
                                  <span className={`block truncate ${formData.state === state ? 'font-semibold' : 'font-normal'}`}>
                                    {state}
                                  </span>
                                  {formData.state === state && (
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600">
                                      <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </div>
                              ))}
                              {filteredStates.length === 0 && (
                                <div className="py-2 px-3 text-gray-500 text-sm">No states found</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* City Dropdown - Custom */}
                      <div className="relative">
                        <label className="block text-sm font-semibold leading-6 text-gray-900">
                          City *
                        </label>
                        <div className="mt-2.5 relative">
                          <button
                            type="button"
                            disabled={!formData.state}
                            onClick={() => {
                              setIsCityOpen(!isCityOpen);
                              setCitySearch("");
                            }}
                            className="relative w-full cursor-default rounded-xl border-0 py-3 pl-11 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm sm:leading-6 bg-gray-50 focus:bg-white transition-all disabled:bg-gray-100 disabled:text-gray-400"
                          >
                            <MapPinIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <span className={`block truncate ${!formData.city && "text-gray-500"}`}>
                              {formData.city || "Select City"}
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            </span>
                          </button>

                          {isCityOpen && (
                            <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                              <div className="sticky top-0 z-10 bg-white px-2 py-2 border-b border-gray-100">
                                <input
                                  type="text"
                                  autoFocus
                                  className="block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-600 sm:text-sm sm:leading-6"
                                  placeholder="Search city..."
                                  value={citySearch}
                                  onChange={(e) => setCitySearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              {filteredCities.map((city) => (
                                <div
                                  key={city}
                                  className="relative cursor-default select-none py-2 pl-3 pr-9 hover:bg-orange-50 hover:text-orange-900 text-gray-900 cursor-pointer"
                                  onClick={() => {
                                    selectCity(city);
                                    setCitySearch("");
                                  }}
                                >
                                  <span className={`block truncate ${formData.city === city ? 'font-semibold' : 'font-normal'}`}>
                                    {city}
                                  </span>
                                  {formData.city === city && (
                                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600">
                                      <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </div>
                              ))}
                              {filteredCities.length === 0 && (
                                <div className="py-2 px-3 text-gray-500 text-sm">No cities found</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="expertise"
                          className="block text-sm font-semibold leading-6 text-gray-900"
                        >
                          Expertise *
                        </label>
                        <div className="mt-2.5 relative">
                          <button
                            type="button"
                            onClick={() => setIsExpertiseOpen(!isExpertiseOpen)}
                            className="relative w-full cursor-default rounded-xl border-0 py-3 pl-11 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-inset focus:ring-orange-500 sm:text-sm sm:leading-6 bg-gray-50 focus:bg-white transition-all"
                          >
                            <SparklesIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <span className={`block truncate ${formData.expertise.length === 0 && "text-gray-500"}`}>
                              {formData.expertise.length > 0
                                ? `${formData.expertise.length} selected`
                                : "Select Expertise"
                              }
                            </span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                            </span>
                          </button>

                          {isExpertiseOpen && (
                            <>
                              <div
                                className="fixed inset-0 z-10"
                                onClick={() => setIsExpertiseOpen(false)}
                              />
                              <div className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                                {EXPERTISE_OPTIONS.map((option) => {
                                  const isSelected = formData.expertise.includes(option.value);
                                  return (
                                    <div
                                      key={option.value}
                                      className={`relative cursor-pointer select-none py-2 pl-3 pr-9 hover:bg-orange-50 hover:text-orange-900 text-gray-900 ${isSelected ? 'bg-orange-50/50' : ''}`}
                                      onClick={() => handleExpertiseToggle(option.value)}
                                    >
                                      <span className={`block truncate ${isSelected ? 'font-semibold text-orange-600' : 'font-normal'}`}>
                                        {option.label}
                                      </span>
                                      {isSelected && (
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-orange-600">
                                          <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="yearsOfExperience"
                          className="block text-sm font-semibold leading-6 text-gray-900"
                        >
                          Experience (Years)
                        </label>
                        <div className="mt-2.5">
                          <input
                            type="number"
                            name="yearsOfExperience"
                            id="yearsOfExperience"
                            min="0"
                            value={formData.yearsOfExperience}
                            onChange={(e) =>
                              handleInputChange("yearsOfExperience", e.target.value)
                            }
                            className="block w-full rounded-xl border-0 py-3 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-orange-500 bg-gray-50 focus:bg-white transition-all sm:text-sm sm:leading-6"
                            placeholder="e.g. 2"
                          />
                        </div>
                      </div>




                    </div>

                    {/* Transparency Notice */}
                    <div className="rounded-md bg-green-50 p-4 border border-green-100">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CurrencyDollarIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-green-800">Transparent Earnings Model</h3>
                          <div className="mt-2 text-sm text-green-700">
                            <p>
                              Instructors currently receive 80% of the class fee for bookings completed through the platform.
                            </p>
                            <p className="mt-1">
                              Relaxing Routine retains a 20% platform fee to cover payment processing, technology, and operational support.
                            </p>
                            {/* <p className="mt-2 text-xs text-green-600 italic">
                              * Platform fees may change with prior notice.
                            </p> */}
                          </div>
                        </div>
                      </div>
                    </div>



                    {/* Legal Agreements */}
                    <div className="space-y-3 pt-2">
                      <label className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={formData.termsAccepted}
                          onChange={(e) =>
                            handleInputChange("termsAccepted", e.target.checked)
                          }
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 transition-colors"
                        />
                        <span className="text-sm text-gray-600">
                          I agree to the{" "}
                          <button
                            type="button"
                            onClick={() => {
                              setLegalDocType("terms");
                              setShowLegalModal(true);
                            }}
                            className="text-orange-600 hover:text-orange-700 underline font-medium"
                          >
                            Instructor Terms & Conditions
                          </button>{" "}
                          and{" "}
                          <button
                            type="button"
                            onClick={() => {
                              setLegalDocType("privacy");
                              setShowLegalModal(true);
                            }}
                            className="text-orange-600 hover:text-orange-700 underline font-medium"
                          >
                            Privacy Policy
                          </button>
                        </span>
                      </label>
                    </div>

                    <div className="mt-8 flex justify-end">
                      <button
                        type="submit"
                        disabled={loading}
                        className="block w-full rounded-md bg-orange-600 px-3.5 py-3.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? (
                          "Processing..."
                        ) : (
                          "Submit Application"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <LegalDocumentModal
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        documentType={legalDocType}
      />
    </>
  );
};

export default InstructorLandingPage;
